/**
 * LogoDoor — the brand-mark component, wrapping the real platform
 * artwork at `/public/brand/logo.png`.
 *
 * The PNG IS the brand. The artwork already depicts the door open
 * (orange roof, black house body, hinged orange door, calendar,
 * green checkmark, green sparkle), so the "logo opens the door for
 * entry" metaphor is carried by the static art on its own. This
 * component does NOT redraw the logo in primitives — that earlier
 * approach was a shortcut and the wrong call. It loads the PNG.
 *
 * Interaction:
 *   - Without `onClick` → renders a static `<span>` containing an
 *     `<img>` with `alt={ariaLabel}`.
 *   - With `onClick`   → renders a real `<button>` with the alt /
 *     aria-label set, a subtle hover/focus scale-up to signal
 *     interactivity, and the click handler firing immediately.
 *   - `prefers-reduced-motion: reduce` skips the scale animation
 *     while keeping the click affordance (WCAG 2.3.3).
 *
 * The tasteful scale-up on hover is the affordance, not a fake
 * pixel-level door rotation. If a more dramatic page-transition is
 * desired (e.g., double-door wipe revealing /login), that lives at
 * the route layer — not inside this component.
 */

import {
  type CSSProperties,
  type ReactNode,
  useState,
  useSyncExternalStore,
}                          from 'react'

const ANIMATION_DURATION_MS = 220
const ANIMATION_TIMING      = 'cubic-bezier(0.34, 1.56, 0.64, 1)'
const HOVER_SCALE           = 1.06

const LOGO_SRC = '/brand/logo.png'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * `prefers-reduced-motion` is browser state, not React state — mirroring it
 * into `useState` from an effect means the first paint always animates and
 * only then corrects itself. `useSyncExternalStore` reads it during render
 * instead, so a reduced-motion user never sees the animated frame.
 *
 * Guards for `window`/`matchMedia` being absent: the prerender step
 * (`scripts/prerender.mjs`) renders this component under Node, where neither
 * exists. The server snapshot is `false` — "no preference expressed".
 */
function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const mq = window.matchMedia(REDUCED_MOTION_QUERY)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

function getReducedMotionSnapshot(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia(REDUCED_MOTION_QUERY).matches
}

function getReducedMotionServerSnapshot(): boolean {
  return false
}

export interface LogoDoorProps {
  /** Pixel width/height of the rendered logo. */
  size?:      number
  /** Accessible name. Used as the `<img alt>` and the button aria-label. */
  ariaLabel?: string
  /**
   * Click / Enter / Space handler. When provided, the component
   * renders as a `<button>` with hover + focus animation. When
   * omitted, it renders as a non-interactive `<span>`.
   */
  onClick?:   () => void
}

export function LogoDoor({
  size      = 96,
  ariaLabel = 'ReadyRoomOps',
  onClick,
}: LogoDoorProps): ReactNode {
  const interactive = typeof onClick === 'function'
  const [active, setActive] = useState(false)

  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  )

  const animateScale = interactive && active && !reducedMotion

  const wrapperStyle: CSSProperties = {
    display:    'inline-block',
    width:      size,
    height:     size,
    background: 'transparent',
    border:     'none',
    padding:    0,
    margin:     0,
    cursor:     interactive ? 'pointer' : 'default',
    lineHeight: 0,
    transform:  animateScale ? `scale(${HOVER_SCALE})` : 'none',
    transition: reducedMotion
      ? 'none'
      : `transform ${ANIMATION_DURATION_MS}ms ${ANIMATION_TIMING}`,
  }

  const imgStyle: CSSProperties = {
    width:        '100%',
    height:       '100%',
    objectFit:    'contain',
    display:      'block',
    userSelect:   'none',
  }

  const interactiveProps = interactive ? {
    type:         'button' as const,
    onClick,
    onMouseEnter: () => setActive(true),
    onMouseLeave: () => setActive(false),
    onFocus:      () => setActive(true),
    onBlur:       () => setActive(false),
    'aria-label': ariaLabel,
    'data-state': active ? 'active' : 'idle',
  } : {}

  const img = (
    <img
      src={LOGO_SRC}
      alt={interactive ? '' : ariaLabel}
      width={size}
      height={size}
      style={imgStyle}
      draggable={false}
      data-testid="logo-door-img"
    />
  )

  if (interactive) {
    return (
      <button style={wrapperStyle} {...interactiveProps}>
        {img}
      </button>
    )
  }

  return (
    <span style={wrapperStyle} role="presentation">
      {img}
    </span>
  )
}
